import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Save, CheckCircle, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useUpdateBookingOption } from "@/hooks/useBookings";
import {
  BookingOptionResponse,
  BookingOptionStatus,
  UpdateBookingOptionRequest,
  getBookingOptionStatusColor
} from "@/types/activities";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

interface UpdateBookingOptionModalProps {
  open: boolean;
  onClose: () => void;
  bookingId: string;
  option: BookingOptionResponse;
  onSuccess?: () => void;
}

interface FormData extends Omit<UpdateBookingOptionRequest, 'isAvailable'> {
  isAvailable: 'true' | 'false';
}

export default function UpdateBookingOptionModal({
  open,
  onClose,
  bookingId,
  option,
  onSuccess
}: UpdateBookingOptionModalProps) {
  const { toast } = useToast();
  const updateBookingOption = useUpdateBookingOption();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: {
      isAvailable: 'true',
      unavailableReason: "",
      proposedPrice: undefined,
      proposedStartDate: "",
      proposedEndDate: "",
      proposedLocation: "",
      proposedCapacity: undefined,
      cancellationPolicy: "",
      specialTerms: "",
      internalNotes: ""
    }
  });

  const isAvailableValue = watch("isAvailable");

  // Reset form when modal opens
  useEffect(() => {
    if (open && option) {
      reset({
        isAvailable: 'true',
        unavailableReason: "",
        proposedPrice: undefined,
        proposedStartDate: "",
        proposedEndDate: "",
        proposedLocation: "",
        proposedCapacity: undefined,
        cancellationPolicy: "",
        specialTerms: "",
        internalNotes: ""
      });
    }
  }, [open, option, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      const isAvailable = data.isAvailable === 'true';

      // Validate required fields based on availability
      if (isAvailable && !data.proposedPrice) {
        toast({
          title: "Validation Error",
          description: "Proposed price is required when vendor is available.",
          variant: "destructive"
        });
        return;
      }

      if (!isAvailable && !data.unavailableReason?.trim()) {
        toast({
          title: "Validation Error",
          description: "Unavailable reason is required when vendor is not available.",
          variant: "destructive"
        });
        return;
      }

      // Format dates for API
      const formattedData: UpdateBookingOptionRequest = {
        isAvailable,
        unavailableReason: !isAvailable ? data.unavailableReason : undefined,
        proposedPrice: isAvailable ? data.proposedPrice : undefined,
        proposedStartDate: isAvailable && data.proposedStartDate ? new Date(data.proposedStartDate).toISOString() : undefined,
        proposedEndDate: isAvailable && data.proposedEndDate ? new Date(data.proposedEndDate).toISOString() : undefined,
        proposedLocation: isAvailable ? data.proposedLocation : undefined,
        proposedCapacity: isAvailable ? data.proposedCapacity : undefined,
        cancellationPolicy: isAvailable ? data.cancellationPolicy : undefined,
        specialTerms: isAvailable ? data.specialTerms : undefined,
        internalNotes: data.internalNotes
      };

      await updateBookingOption.mutateAsync({
        bookingId,
        optionId: option.id,
        data: formattedData
      });

      toast({
        title: "Vendor Response Submitted",
        description: isAvailable
          ? "Option marked as available and sent for organization approval."
          : "Option marked as unavailable."
      });

      onSuccess?.();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update booking option. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const isAvailable = isAvailableValue === 'true';
  const isUnavailable = isAvailableValue === 'false';

  // Only allow updates when option status is Exploring
  if (option.status !== BookingOptionStatus.Exploring) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cannot Update Option</DialogTitle>
            <DialogDescription>
              Vendor responses can only be submitted when the option status is "Exploring".
              This option has status: {option.status}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Save className="h-5 w-5" />
            Submit Vendor Response
          </DialogTitle>
          <DialogDescription>
            Option #{option.optionOrder}: {option.serviceName} - {option.vendorName}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Current Status */}
          <div className="space-y-2">
            <Label>Current Status</Label>
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className={`${getBookingOptionStatusColor(option.status)} text-xs`}
              >
                {option.status}
              </Badge>
              <span className="text-sm text-muted-foreground">
                Created: {new Date(option.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>

          <Separator />

          {/* Important Info Alert */}
          {isAvailable && (
            <Alert className="bg-yellow-50 border-yellow-200">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800 text-sm">
                <strong>⚠️ Important:</strong> Marking this option as "Available" will:
                <ul className="list-disc ml-5 mt-2 space-y-1">
                  <li>Automatically change the booking status to <strong>"Pending Organization Approval"</strong></li>
                  <li>Show this option to the organization in their client portal</li>
                  <li>Allow the organization to approve, reject, or counter this proposal</li>
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Vendor Response */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Is this option available at the vendor?</h3>

            <RadioGroup
              value={isAvailableValue}
              onValueChange={(value) => setValue("isAvailable", value as 'true' | 'false')}
              className="space-y-3"
            >
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-green-50 cursor-pointer">
                <RadioGroupItem value="true" id="available" />
                <Label htmlFor="available" className="flex items-center gap-2 cursor-pointer flex-1">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <div>
                    <div className="font-medium">Yes - Vendor is Available</div>
                    <div className="text-xs text-muted-foreground">Provide pricing and details</div>
                  </div>
                </Label>
              </div>

              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-red-50 cursor-pointer">
                <RadioGroupItem value="false" id="unavailable" />
                <Label htmlFor="unavailable" className="flex items-center gap-2 cursor-pointer flex-1">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <div>
                    <div className="font-medium">No - Vendor is Unavailable</div>
                    <div className="text-xs text-muted-foreground">Provide reason for unavailability</div>
                  </div>
                </Label>
              </div>
            </RadioGroup>

            {/* Available Status Fields */}
            {isAvailable && (
              <div className="space-y-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="text-sm font-medium text-green-800">✅ Vendor Available - Provide Details</h4>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="proposedPrice">Proposed Price (AED) *</Label>
                    <Input
                      id="proposedPrice"
                      type="number"
                      min="0"
                      step="0.01"
                      {...register("proposedPrice", { valueAsNumber: true })}
                      placeholder="25000"
                      required={isAvailable}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="proposedCapacity">Proposed Capacity</Label>
                    <Input
                      id="proposedCapacity"
                      type="number"
                      min="1"
                      {...register("proposedCapacity", { valueAsNumber: true })}
                      placeholder="50"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="proposedStartDate">Proposed Start Date & Time</Label>
                    <Input
                      id="proposedStartDate"
                      type="datetime-local"
                      {...register("proposedStartDate")}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="proposedEndDate">Proposed End Date & Time</Label>
                    <Input
                      id="proposedEndDate"
                      type="datetime-local"
                      {...register("proposedEndDate")}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="proposedLocation">Proposed Location</Label>
                  <Input
                    id="proposedLocation"
                    {...register("proposedLocation")}
                    placeholder="Vendor Training Center, Dubai"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="specialTerms">Special Terms & Conditions</Label>
                  <Textarea
                    id="specialTerms"
                    {...register("specialTerms")}
                    placeholder="Includes lunch, materials, certificates..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cancellationPolicy">Cancellation Policy</Label>
                  <Textarea
                    id="cancellationPolicy"
                    {...register("cancellationPolicy")}
                    placeholder="48 hours notice required for cancellation..."
                    rows={2}
                  />
                </div>
              </div>
            )}

            {/* Unavailable Status Fields */}
            {isUnavailable && (
              <div className="space-y-4 p-4 bg-red-50 rounded-lg border border-red-200">
                <h4 className="text-sm font-medium text-red-800">❌ Vendor Unavailable - Provide Reason</h4>

                <div className="space-y-2">
                  <Label htmlFor="unavailableReason">Unavailable Reason *</Label>
                  <Textarea
                    id="unavailableReason"
                    {...register("unavailableReason")}
                    placeholder="Venue fully booked for preferred dates, no trainers available, service not offered during this period..."
                    rows={3}
                    required={isUnavailable}
                  />
                </div>
              </div>
            )}

            {/* Internal Notes */}
            <div className="space-y-2">
              <Label htmlFor="internalNotes">Internal Notes (Optional)</Label>
              <Textarea
                id="internalNotes"
                {...register("internalNotes")}
                placeholder="Internal notes about this vendor response (not shared with organization or vendor)..."
                rows={2}
              />
              <p className="text-xs text-muted-foreground">
                These notes are for Athlyn team use only and will not be shared externally.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateBookingOption.isPending}
              className="flex items-center gap-2"
            >
              {updateBookingOption.isPending ? (
                "Submitting..."
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Submit Vendor Response
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}